/**
 * AI API - AI-powered features for the CRM
 */
import apiClient from './index'
import type { ApiResponse } from '@/types'

export interface NoteSummaryRequest {
  entity_type: 'contact' | 'company' | 'deal'
  entity_id: string
  context?: string
}

export interface NoteSummaryResponse {
  summary: string
}

export interface EmailDraftRequest {
  purpose: string
  recipient_name: string
  recipient_context?: string
  tone?: 'professional' | 'friendly' | 'formal' | 'casual'
  additional_context?: string
}

export interface EmailDraftResponse {
  subject: string
  body: string
}

export interface ActivityInsightsRequest {
  entity_type: 'contact' | 'company' | 'deal'
  entity_id: string
  entity_name: string
}

export interface ActivityInsightsResponse {
  insights: string
}

export interface EntitySummaryRequest {
  entity_type: 'contact' | 'company' | 'deal'
  entity_id: string
}

export interface EntitySummaryResponse {
  summary: string
}

export interface SuggestActionsRequest {
  entity_type: 'contact' | 'deal'
  entity_id: string
}

export interface ActionSuggestion {
  action: string
  reason: string
}

export interface SuggestActionsResponse {
  suggestions: ActionSuggestion[]
}

// Cache Metadata - returned with cached AI briefings
export interface CacheMetadata {
  generated_at: string | null
  generated_by_name: string
  cache_date?: string
  is_cached: boolean
}

// Daily Briefing Types
export interface DailyBriefingPriority {
  item: string
  reason: string
  urgency: 'high' | 'medium' | 'low'
}

export interface DailyBriefingRisk {
  deal_name: string
  concern: string
  suggested_action: string
}

export interface DailyBriefingOpportunity {
  description: string
  potential_value: string
  next_step: string
}

export interface DailyBriefingResponse {
  summary: string
  priorities: DailyBriefingPriority[]
  risks: DailyBriefingRisk[]
  opportunities: DailyBriefingOpportunity[]
  _cache?: CacheMetadata
}

// Deal Risk Types
export interface DealRisk {
  deal_name: string
  deal_id?: string
  risk_level: 'high' | 'medium' | 'low'
  concern: string
  action: string
}

export interface DealRisksResponse {
  risks: DealRisk[]
  message?: string
}

// Meeting Prep Types
export interface MeetingPrepResponse {
  summary: string
  talking_points: string[]
  questions_to_ask: string[]
  key_background: string[]
  suggested_next_steps: string[]
}

// Smart Search Types
export interface SmartSearchRequest {
  query: string
}

export interface SmartSearchResponse {
  interpretation: string
  entity_type: 'contacts' | 'companies' | 'deals' | 'all'
  filters: Record<string, unknown>
  sort_by: string | null
  sort_order: 'asc' | 'desc'
}

// Microsoft 365 AI Types
export interface EmailThreadSummaryRequest {
  contact_email: string
  contact_id?: string
  deal_id?: string
}

export interface EmailThreadSummaryResponse {
  summary: string
  key_points: string[]
  action_items: string[]
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed'
  urgency: 'high' | 'medium' | 'low'
  topics_discussed: string[]
  suggested_response: string | null
  relationship_status: string
  emails_analyzed: number
}

export interface CalendarBriefingResponse {
  summary: string
  key_meetings: Array<{
    title: string
    time: string
    importance: 'high' | 'medium' | 'low'
    prep_note: string
  }>
  time_allocation: {
    meetings_hours: number
    focus_time_available: string
  }
  schedule_insights: string[]
  suggested_priorities: string[]
}

export interface InboxBriefingResponse {
  summary: string
  urgent_emails: Array<{
    from: string
    subject: string
    reason: string
  }>
  crm_relevant: Array<{
    from: string
    subject: string
    suggested_action: string
  }>
  action_required: Array<{
    from: string
    subject: string
    action: string
  }>
  can_wait: string[]
  inbox_health: string
  stats: {
    total: number
    unread: number
    from_crm_contacts: number
  }
}

export interface ComprehensiveBriefingResponse {
  executive_summary: string
  top_priorities: Array<{
    priority: string
    source: 'CRM' | 'Calendar' | 'Email'
    urgency: 'high' | 'medium' | 'low'
    why: string
  }>
  schedule_overview: {
    meeting_load: 'light' | 'moderate' | 'heavy'
    key_meetings: string[]
    focus_time: string
  }
  communications: {
    needs_response: string[]
    follow_ups: string[]
  }
  risks_today: string[]
  opportunities: string[]
  data_sources: {
    crm: boolean
    outlook_calendar?: boolean
    outlook_email?: boolean
    google_calendar?: boolean
    gmail?: boolean
  }
  _cache?: CacheMetadata
}

export interface EnhancedMeetingPrepResponse {
  executive_summary: string
  email_insights: {
    last_topic_discussed: string
    open_threads: string[]
    their_concerns: string[]
    commitments_made: string[]
  }
  talking_points: Array<{
    point: string
    context: string
  }>
  questions_to_ask: string[]
  things_to_avoid: string[]
  documents_to_reference: string[]
  suggested_outcomes: string[]
  follow_up_actions: string[]
  has_email_context: boolean
  emails_analyzed: number
}

export interface SuggestResponseRequest {
  message_id: string
  contact_id?: string
  deal_id?: string
  tone?: 'professional' | 'friendly' | 'formal'
}

export interface SuggestResponseResponse {
  suggested_subject: string
  suggested_response: string
  key_points_addressed: string[]
  tone_used: string
  follow_up_needed: boolean
  crm_action_suggested: string | null
}

export const aiApi = {
  /**
   * Summarize notes for an entity
   */
  async summarizeNotes(data: NoteSummaryRequest): Promise<NoteSummaryResponse> {
    const response = await apiClient.post<ApiResponse<NoteSummaryResponse>>(
      '/ai/summarize-notes',
      data
    )
    return response.data.data
  },

  /**
   * Generate an email draft
   */
  async draftEmail(data: EmailDraftRequest): Promise<EmailDraftResponse> {
    const response = await apiClient.post<ApiResponse<EmailDraftResponse>>(
      '/ai/draft-email',
      data
    )
    return response.data.data
  },

  /**
   * Generate activity insights for an entity
   */
  async getActivityInsights(data: ActivityInsightsRequest): Promise<ActivityInsightsResponse> {
    const response = await apiClient.post<ApiResponse<ActivityInsightsResponse>>(
      '/ai/activity-insights',
      data
    )
    return response.data.data
  },

  /**
   * Generate a comprehensive summary of an entity
   */
  async getEntitySummary(data: EntitySummaryRequest): Promise<EntitySummaryResponse> {
    const response = await apiClient.post<ApiResponse<EntitySummaryResponse>>(
      '/ai/entity-summary',
      data
    )
    return response.data.data
  },

  /**
   * Get suggested follow-up actions for an entity
   */
  async suggestActions(data: SuggestActionsRequest): Promise<SuggestActionsResponse> {
    const response = await apiClient.post<ApiResponse<SuggestActionsResponse>>(
      '/ai/suggest-actions',
      data
    )
    return response.data.data
  },

  /**
   * Get daily briefing with AI-powered insights
   * @param forceRefresh - Set to true to bypass cache and regenerate
   */
  async getDailyBriefing(forceRefresh = false): Promise<DailyBriefingResponse> {
    const params = forceRefresh ? '?force_refresh=true' : ''
    const response = await apiClient.get<ApiResponse<DailyBriefingResponse>>(
      `/ai/daily-briefing${params}`
    )
    return response.data.data
  },

  /**
   * Analyze active deals for risks
   */
  async getDealRisks(): Promise<DealRisksResponse> {
    const response = await apiClient.get<ApiResponse<DealRisksResponse>>(
      '/ai/deal-risks'
    )
    return response.data.data
  },

  /**
   * Get meeting preparation materials
   */
  async getMeetingPrep(meetingId: string): Promise<MeetingPrepResponse> {
    const response = await apiClient.get<ApiResponse<MeetingPrepResponse>>(
      `/ai/meeting-prep/${meetingId}`
    )
    return response.data.data
  },

  /**
   * Smart search with natural language
   */
  async smartSearch(query: string): Promise<SmartSearchResponse> {
    const response = await apiClient.post<ApiResponse<SmartSearchResponse>>(
      '/ai/smart-search',
      { query }
    )
    return response.data.data
  },

  // ============================================
  // Microsoft 365 AI Integration
  // ============================================

  /**
   * Summarize email thread with a contact (requires Microsoft 365 connection)
   */
  async getEmailThreadSummary(data: EmailThreadSummaryRequest): Promise<EmailThreadSummaryResponse> {
    const response = await apiClient.post<ApiResponse<EmailThreadSummaryResponse>>(
      '/ai/microsoft/email-summary',
      data
    )
    return response.data.data
  },

  /**
   * Get AI analysis of today's Outlook calendar
   */
  async getCalendarBriefing(): Promise<CalendarBriefingResponse> {
    const response = await apiClient.get<ApiResponse<CalendarBriefingResponse>>(
      '/ai/microsoft/calendar-briefing'
    )
    return response.data.data
  },

  /**
   * Get AI analysis of inbox, highlighting CRM-relevant emails
   */
  async getInboxBriefing(): Promise<InboxBriefingResponse> {
    const response = await apiClient.get<ApiResponse<InboxBriefingResponse>>(
      '/ai/microsoft/inbox-briefing'
    )
    return response.data.data
  },

  /**
   * Get comprehensive daily briefing combining CRM + Outlook Calendar + Emails
   * This is the ultimate morning briefing that synthesizes all data sources.
   * @param forceRefresh - Set to true to bypass cache and regenerate
   */
  async getComprehensiveBriefing(forceRefresh = false): Promise<ComprehensiveBriefingResponse> {
    const params = forceRefresh ? '?force_refresh=true' : ''
    const response = await apiClient.get<ApiResponse<ComprehensiveBriefingResponse>>(
      `/ai/microsoft/comprehensive-briefing${params}`
    )
    return response.data.data
  },

  /**
   * Get enhanced meeting prep that includes Outlook email history
   */
  async getEnhancedMeetingPrep(meetingId: string): Promise<EnhancedMeetingPrepResponse> {
    const response = await apiClient.get<ApiResponse<EnhancedMeetingPrepResponse>>(
      `/ai/microsoft/meeting-prep/${meetingId}`
    )
    return response.data.data
  },

  /**
   * Suggest a response to an email using CRM context
   */
  async suggestEmailResponse(data: SuggestResponseRequest): Promise<SuggestResponseResponse> {
    const response = await apiClient.post<ApiResponse<SuggestResponseResponse>>(
      '/ai/microsoft/suggest-response',
      data
    )
    return response.data.data
  },

  // ============================================
  // AI Writing Assistant
  // ============================================

  /**
   * Generate text content for various CRM use cases
   */
  async generateContent(data: {
    content_type: 'note' | 'description' | 'task' | 'proposal_section' | 'payment_reminder' | 'meeting_agenda' | 'project_scope'
    context: Record<string, unknown>
    tone?: 'professional' | 'friendly' | 'formal' | 'casual'
    length?: 'short' | 'medium' | 'long'
  }): Promise<{ content: string }> {
    const response = await apiClient.post<ApiResponse<{ content: string }>>(
      '/ai/generate-content',
      data
    )
    return response.data.data
  },

  /**
   * Improve existing text with AI suggestions
   */
  async improveText(data: {
    text: string
    improvement_type?: 'general' | 'clarity' | 'professional' | 'concise' | 'persuasive' | 'friendly'
    context?: string
  }): Promise<{
    improved_text: string
    changes_made: string[]
    suggestions: string[]
  }> {
    const response = await apiClient.post<ApiResponse<{
      improved_text: string
      changes_made: string[]
      suggestions: string[]
    }>>('/ai/improve-text', data)
    return response.data.data
  },

  /**
   * Generate a task breakdown for a project
   */
  async generateProjectTasks(data: {
    project_id?: string
    project_name?: string
    project_description?: string
    scope_description?: string
    project_type?: string
    estimated_duration?: string
    complexity?: 'simple' | 'moderate' | 'complex'
  }): Promise<{
    tasks: Array<{
      title: string
      description: string
      estimated_hours: number
      order?: number
      priority?: 'high' | 'medium' | 'low'
      dependencies?: string[]
      phase: string
    }>
    total_estimated_hours: number
    phases?: string[]
    recommended_timeline?: string
    notes: string | string[]
  }> {
    const response = await apiClient.post<ApiResponse<{
      tasks: Array<{
        title: string
        description: string
        estimated_hours: number
        order?: number
        priority?: 'high' | 'medium' | 'low'
        dependencies?: string[]
        phase: string
      }>
      total_estimated_hours: number
      phases?: string[]
      recommended_timeline?: string
      notes: string | string[]
    }>>('/ai/project-tasks', data)
    return response.data.data
  },

  /**
   * Get AI-suggested next steps for a deal
   */
  async getDealNextSteps(dealId: string): Promise<{
    health_score: number
    health_status: 'on_track' | 'at_risk' | 'needs_attention' | 'stalled'
    immediate_actions: Array<{
      action: string
      reason: string
      priority: 'high' | 'medium' | 'low'
    }>
    talking_points: string[]
    risks: string[]
    opportunities: string[]
    suggested_timeline: string
  }> {
    const response = await apiClient.get<ApiResponse<{
      health_score: number
      health_status: 'on_track' | 'at_risk' | 'needs_attention' | 'stalled'
      immediate_actions: Array<{
        action: string
        reason: string
        priority: 'high' | 'medium' | 'low'
      }>
      talking_points: string[]
      risks: string[]
      opportunities: string[]
      suggested_timeline: string
    }>>(`/ai/deal-next-steps/${dealId}`)
    return response.data.data
  },

  /**
   * Generate a payment reminder email for an invoice
   */
  async getInvoiceReminder(invoiceId: string, tone?: 'friendly' | 'professional' | 'firm' | 'urgent'): Promise<{
    subject: string
    body: string
    tone_used: string
    urgency_level: string
  }> {
    const params = tone ? `?tone=${tone}` : ''
    const response = await apiClient.get<ApiResponse<{
      subject: string
      body: string
      tone_used: string
      urgency_level: string
    }>>(`/ai/invoice-reminder/${invoiceId}${params}`)
    return response.data.data
  },

  /**
   * Summarize a batch of emails (for Email Inbox AI Summary)
   */
  async summarizeEmails(data: {
    contact_email: string
    emails: Array<{
      date: string
      from: string
      subject: string
      preview: string
    }>
  }): Promise<{ summary: string }> {
    const response = await apiClient.post<ApiResponse<{ summary: string }>>(
      '/ai/summarize-emails',
      data
    )
    return response.data.data
  },

  /**
   * Generate meeting prep with attendee context (for Calendar AI Meeting Prep)
   */
  async generateMeetingPrep(data: {
    event_subject: string
    event_date: string
    attendees: string[]
    contact_data?: Array<{
      id?: string
      first_name?: string
      last_name?: string
      email?: string
      company_name?: string
      job_title?: string
    }>
  }): Promise<{
    attendee_insights?: Array<{
      email: string
      name?: string
      title?: string
      summary?: string
    }>
    talking_points?: string[]
    questions_to_ask?: string[]
    preparation_checklist?: string[]
    summary?: string
    error?: string
  }> {
    const response = await apiClient.post<ApiResponse<{
      attendee_insights?: Array<{
        email: string
        name?: string
        title?: string
        summary?: string
      }>
      talking_points?: string[]
      questions_to_ask?: string[]
      preparation_checklist?: string[]
      summary?: string
      error?: string
    }>>('/ai/generate-meeting-prep', data)
    return response.data.data
  },

  /**
   * Analyze engagement with a contact
   */
  async getContactEngagement(contactId: string): Promise<{
    engagement_score: number
    engagement_level: 'high' | 'medium' | 'low' | 'cold'
    relationship_health: 'strong' | 'healthy' | 'needs_attention' | 'at_risk'
    key_insights: string[]
    recommended_actions: Array<{
      action: string
      timing: string
      reason: string
    }>
    best_contact_method: string
    optimal_contact_frequency: string
    risk_factors: string[]
    opportunities: string[]
  }> {
    const response = await apiClient.get<ApiResponse<{
      engagement_score: number
      engagement_level: 'high' | 'medium' | 'low' | 'cold'
      relationship_health: 'strong' | 'healthy' | 'needs_attention' | 'at_risk'
      key_insights: string[]
      recommended_actions: Array<{
        action: string
        timing: string
        reason: string
      }>
      best_contact_method: string
      optimal_contact_frequency: string
      risk_factors: string[]
      opportunities: string[]
    }>>(`/ai/contact-engagement/${contactId}`)
    return response.data.data
  },
}
